import CHARM_VAULT_ABI from 'abis/CharmVault.json'
import { useMemo } from 'react'
import { formatUnits, maxUint256 } from 'viem'
import { Abi, Address } from 'viem'

import { useReadContractMultiple } from './useReadContractMultiple'

export const useCharmDepositCap = (poolAddress?: Address, tvlUSD?: number) => {
  const contracts = useMemo(() => {
    if (!poolAddress) return []
    return [
      {
        address: poolAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'maxTotalSupply',
      },
      {
        address: poolAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'totalSupply',
      },
      {
        address: poolAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'decimals',
      },
    ]
  }, [poolAddress])

  const { data, isLoading } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0,
    },
  })

  const maxTotalSupplyResult = data?.[0]
  const totalSupplyResult = data?.[1]
  const decimalsResult = data?.[2]

  const capData = useMemo(() => {
    if (
      !maxTotalSupplyResult ||
      maxTotalSupplyResult.status !== 'success' ||
      !totalSupplyResult ||
      totalSupplyResult.status !== 'success' ||
      !decimalsResult ||
      decimalsResult.status !== 'success' ||
      tvlUSD === undefined
    ) {
      return null
    }

    try {
      const maxTotalSupplyBigint = maxTotalSupplyResult.result as bigint
      const totalSupplyBigint = totalSupplyResult.result as bigint

      const decimals = Number(decimalsResult.result) || 18

      if (maxTotalSupplyBigint === maxUint256) {
        return null
      }

      const maxCapTokensString = formatUnits(maxTotalSupplyBigint, decimals)
      const currentDepositTokensString = formatUnits(totalSupplyBigint, decimals)

      const maxCapTokens = parseFloat(maxCapTokensString)
      const currentDepositTokens = parseFloat(currentDepositTokensString)

      const pricePerToken = currentDepositTokens > 0 ? tvlUSD / currentDepositTokens : 0

      const maxCapUSD = maxCapTokens * pricePerToken
      const currentDepositsUSD = tvlUSD

      return {
        maxCap: maxCapUSD,
        currentDeposits: currentDepositsUSD,
        maxCapTokens,
        currentDepositTokens,
        pricePerToken,
      }
    } catch (error) {
      return null
    }
  }, [maxTotalSupplyResult, totalSupplyResult, decimalsResult, tvlUSD])

  return { capData, isLoading }
}
